import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, MenuItem, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Pagination, InputAdornment, Checkbox, Popover, FormControlLabel, Tooltip, Alert, CircularProgress, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import CalculateIcon from '@mui/icons-material/Calculate';
import api from '../../api';

// Fixed UTC timezone issue by formatting to local date explicitly
const getDefaultDates = () => {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  
  const formatLocal = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  return { startDate: formatLocal(firstDay), endDate: formatLocal(yesterday) };
};

const defaultDates = getDefaultDates();

// Updated column configuration with WO added and NH/T.CORR moved to hidden
const ALL_COLUMNS = [
  // Sticky/Frozen Columns (Always Visible by default)
  { id: 'emp_id', label: 'EMP ID', defaultVisible: true, sticky: true },
  { id: 'employee_name', label: 'EMPLOYEE NAME', defaultVisible: true, sticky: true },
  { id: 'department', label: 'DEPARTMENT', defaultVisible: true, sticky: true },
  
  // Default display fields
  { id: 'total_days', label: 'T.DAYS', defaultVisible: true },
  { id: 'paydays', label: 'PAY.DAYS', defaultVisible: true },
  { id: 'present_total', label: 'Present', defaultVisible: true },
  { id: 'half_days', label: 'HD', defaultVisible: true },
  { id: 'leaves_total', label: 'T.LEAVES', defaultVisible: true },
  { id: 'wo', label: 'WO', defaultVisible: true }, // Added WO as default display
  { id: 'lops_total', label: 'LOPs', defaultVisible: true },
  
  // Hidden display fields for Add/remove section
  { id: 'status', label: 'Status', defaultVisible: false },
  { id: 'designation', label: 'Designation', defaultVisible: false },
  { id: 'l1_manager_name', label: 'L1 Manager', defaultVisible: false },
  { id: 'date_of_join', label: 'Date of Join', defaultVisible: false },
  { id: 'toh', label: 'TOH (Hrs)', defaultVisible: false },
  { id: 'twh', label: 'TWH (Hrs)', defaultVisible: false },
  { id: 'cl', label: 'CL', defaultVisible: false },
  { id: 'ml', label: 'ML', defaultVisible: false },
  { id: 'al', label: 'AL', defaultVisible: false },
  { id: 'el', label: 'EL', defaultVisible: false },
  { id: 'lop', label: 'LOP', defaultVisible: false },
  { id: 'dlop', label: 'DLOP', defaultVisible: false },
  { id: 'present_standard', label: 'Present (Standard) - PR.STD', defaultVisible: false },
  { id: 'present_grace', label: 'Present (Grace) - PR.GRACE', defaultVisible: false },
  { id: 'nh', label: 'NH', defaultVisible: false }, // Moved to hidden
  { id: 'total_corrections', label: 'T.CORR', defaultVisible: false } // Moved to hidden
];

const PayDaysReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [startDate, setStartDate] = useState(defaultDates.startDate);
  const [endDate, setEndDate] = useState(defaultDates.endDate);
  const [search, setSearch] = useState('');
  
  const [departmentList, setDepartmentList] = useState([]);
  const [department, setDepartment] = useState('ALL');
  const [empStatus, setEmpStatus] = useState('ALL'); 
  
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 15;

  const [visibleColumns, setVisibleColumns] = useState(ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.id));
  const [colMenuAnchor, setColMenuAnchor] = useState(null);

  useEffect(() => {
    const fetchDepts = async () => {
      try {
        const res = await api.get('/reports/departments');
        setDepartmentList(res.data);
      } catch (err) {
        console.error("Failed to load departments", err);
      }
    };
    fetchDepts();
  }, []);

  useEffect(() => {
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, department, empStatus, startDate, endDate]);

  const fetchReportData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/reports/paydays', {
        params: {
          start_date: startDate, end_date: endDate, search: search,
          department: department, designation: 'ALL', emp_status: empStatus,
          page: page, limit: limit
        }
      });
      setData(res.data.data);
      setTotalPages(res.data.total_pages);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || "Failed to fetch report.";
      setError(errorMsg);
      console.error("Report Error:", errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    if(e.key === 'Enter') {
      setPage(1);
      fetchReportData();
    }
  };

  const exportToExcel = async () => {
    try {
      const res = await api.get('/reports/paydays', {
        params: {
          start_date: startDate, end_date: endDate, search: search, 
          department: department, designation: 'ALL', emp_status: empStatus, export: true
        }
      });
      
      const exportData = res.data;
      if (!exportData || exportData.length === 0) {
        alert("No data available to export.");
        return;
      }

      const headerRow = ALL_COLUMNS.filter(c => visibleColumns.includes(c.id)).map(c => c.label).join(',');
      const csvRows = exportData.map(row => {
        return ALL_COLUMNS.filter(c => visibleColumns.includes(c.id)).map(col => {
          return `"${row[col.id] !== null && row[col.id] !== undefined ? row[col.id] : ''}"`;
        }).join(',');
      });

      const csvString = [headerRow, ...csvRows].join('\n');
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `PayDays_Report_${startDate}_to_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Failed to export data", err);
      alert("Error exporting data. Please try again.");
    }
  };
  
  // Helper to dynamically calculate left offsets for sticky columns to avoid overlap
  const getStickyLeftOffset = (colId) => {
    if (colId === 'emp_id') return 0;
    if (colId === 'employee_name') return 120; // Approx width of Emp ID
    if (colId === 'department') return 320; // Approx width of Emp ID + Name
    return 0;
  };
  
  const getStickyStyles = (col, isHeader = false) => {
    if (!col.sticky) return {};
    return {
      position: 'sticky',
      left: getStickyLeftOffset(col.id),
      backgroundColor: isHeader ? '#f8fafc' : '#ffffff',
      zIndex: isHeader ? 3 : 1, // Header needs to stay above row content
      borderRight: col.id === 'department' ? '2px solid #cbd5e1' : 'inherit', // Visual separator for the frozen section
      boxShadow: col.id === 'department' ? '4px 0 6px -4px rgba(0,0,0,0.1)' : 'none'
    };
  };

  return (
    <Box sx={{ width: '100%' }}>
      
      {/* Header with Icon */}

      {/* Filter and Control Area */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#ffffff', mb: 4, border: '1px solid #e0e0e0' }} elevation={0}>
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        {/* Re-ordered and compacted fields */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          
          <TextField
            size="small"
            placeholder="Search Emp ID / Name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearchSubmit}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ flexGrow: 1, minWidth: 180 }}
          />

          <TextField label="Start Date" type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 130 }} />
          <TextField label="End Date" type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 130 }} />
          
          <TextField select label="Status" size="small" value={empStatus} onChange={(e) => { setEmpStatus(e.target.value); setPage(1); }} sx={{ minWidth: 110 }}>
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="NOTICE">Notice</MenuItem>
          </TextField>

          <TextField select label="Department" size="small" value={department} onChange={(e) => { setDepartment(e.target.value); setPage(1); }} sx={{ minWidth: 140 }}>
            <MenuItem value="ALL">All Departments</MenuItem>
            {departmentList.map(dept => (
              <MenuItem key={dept} value={dept}>{dept}</MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, borderLeft: '1px solid #e0e0e0', pl: 1.5 }}>
            {loading ? (
              <CircularProgress size={24} sx={{ mx: 1 }} />
            ) : (
              <>
                <Tooltip title="Show/Hide Columns">
                  <IconButton color="primary" onClick={(e) => setColMenuAnchor(e.currentTarget)}>
                    <ViewColumnIcon />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Export to CSV">
                  <IconButton color="success" onClick={exportToExcel}>
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
          
          {/* Columns Popover Menu */}
          <Popover
            open={Boolean(colMenuAnchor)}
            anchorEl={colMenuAnchor}
            onClose={() => setColMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 400, overflowY: 'auto', minWidth: 200, bgcolor: '#ffffff' }}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, fontWeight: 'bold' }}>Manage Columns</Typography>
              {ALL_COLUMNS.map((col) => (
                <FormControlLabel
                  key={col.id}
                  disabled={col.sticky} // Prevent hiding frozen columns
                  control={
                    <Checkbox 
                      size="small" 
                      checked={visibleColumns.includes(col.id)} 
                      onChange={(e) => {
                        if (e.target.checked) setVisibleColumns([...visibleColumns, col.id]);
                        else setVisibleColumns(visibleColumns.filter(id => id !== col.id));
                      }} 
                    />
                  }
                  label={<Typography variant="body2">{col.label}</Typography>}
                />
              ))}
            </Box>
          </Popover>
        </Box>
      </Paper>

      {/* DATA TABLE */}
      <Paper variant="outlined" sx={{ borderRadius: 2, bgcolor: '#ffffff', overflow: 'hidden', border: '1px solid #e0e0e0' }} elevation={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #eee', bgcolor: '#f9fafb' }}>
          <Typography variant="subtitle2" fontWeight="bold" color="#444">Results Overview</Typography>
        </Box>
        <TableContainer sx={{ overflowX: 'auto', maxHeight: '60vh' }}>
          <Table size="small" stickyHeader sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                {ALL_COLUMNS.filter(c => visibleColumns.includes(c.id)).map((col) => (
                  <TableCell 
                    key={col.id} 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: '#555', 
                      whiteSpace: 'nowrap',
                      minWidth: col.id === 'emp_id' ? 120 : (col.id === 'employee_name' ? 200 : (col.id === 'department' ? 180 : 'auto')),
                      ...getStickyStyles(col, true)
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length > 0 ? data.map((row, index) => (
                <TableRow key={index} hover>
                  {ALL_COLUMNS.filter(c => visibleColumns.includes(c.id)).map((col) => (
                    <TableCell 
                      key={`${index}-${col.id}`} 
                      sx={{ 
                        color: '#444', 
                        whiteSpace: 'nowrap',
                        ...getStickyStyles(col, false)
                      }}
                    >
                      {row[col.id] !== null && row[col.id] !== undefined ? row[col.id] : '--'}
                    </TableCell>
                  ))}
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {loading ? 'Fetching report data...' : (error ? 'Unable to load data due to an error.' : 'No employees found matching these filters.')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {data.length > 0 && (
          <Box sx={{ borderTop: '1px solid #eee', bgcolor: '#ffffff', p: 1 }}>
            <Pagination 
              count={totalPages} 
              page={page} 
              onChange={(e, value) => setPage(value)} 
              color="primary" 
              sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}
            />
          </Box>
        )}
      </Paper>
      
    </Box>
  );
};

export default PayDaysReport;